import type { LC, PropsWithChildren, ArrowFunction } from '@use-gpu/live';

import { gather, yeet, unquote, useMemo, useOne, useRef, useCallback } from '@use-gpu/live';
import { useAnimationFrame, useNoAnimationFrame } from '../providers/loop-provider';
import { usePerViewFrame } from '../providers/view-frame-provider';
import { QueueReconciler } from '../reconcilers/index';

const {reconcile, quote, signal} = QueueReconciler;

export type LoopProps = PropsWithChildren<{
  live?: boolean,
  decimate?: number,
  converge?: number,
}>;

export const Loop: LC<LoopProps> = (props: LoopProps) => {
  const {
    live = false,
    decimate = 1,
    converge = 1,
    children,
  } = props;

  const frameRef = useRef(0);
  const remainingRef = useRef(converge);

  // Reset remaining frames if view changes
  const viewFrame = usePerViewFrame();
  useOne(() => remainingRef.current = converge, viewFrame);

  // Request new animation frame as long as frames remain
  if (live || remainingRef.current > 0) useAnimationFrame();
  else useNoAnimationFrame();

  const Resume = useCallback((ts: ArrowFunction[]) => {

    // Reset remaining frames (as draw calls were changed or were signaled)
    remainingRef.current = converge;

    return [
      signal(),
      useMemo(() => {
        const run = () => {
          const {current: frame} = frameRef;

          // Only render frame if not decimated
          if (decimate <= 1 || frame === 0) {
            for (const fn of ts) fn();
            remainingRef.current--;
          }

          frameRef.current = (frameRef.current + 1) % (decimate || 1);
        };

        return quote(yeet(run));
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, ts),
    ];
  }, [decimate, converge]);

  return useMemo(() => (
    reconcile(
      quote(
        gather(
          unquote(
            children
          ),
          Resume,
        )
      )
    )
  ), [Resume, children]);
};
